import { LoaderFunction } from 'react-router-dom';

import {
  getMyLearning,
  getRecentlyViewedLessons,
  MyLearningItem,
  MyLearningData,
  RecentlyViewedLessonItem,
  getArchivedContent,
} from 'src/services/http/myLearning';
import { fetchGraphQL } from 'src/services/contentful/client';
import { GetAllMyLearningEntriesQuery, GetAllMyLearningEntriesQueryVariables } from 'src/graphql/generated/graphql';
import {
  CollectedDataItem,
  CourseEntry,
  GET_ALL_MY_LEARNING_ENTRIES,
  LearningPathEntry,
  LessonEntry,
  MatchingDataItem,
} from 'src/graphql/queries/GetAllMyLearningEntries';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';
import { getContentProduct } from 'src/utils/getContentProduct';
import { getSlug } from 'src/utils/slug';
import { NotificationItem } from 'src/services/http/notifications';
import { getProgressForLessons, LearningProgressStatus } from 'src/services/http/progress';
import { FlattenedLearningInstance } from 'src/utils/almDataTransformer';

import { allTrainingSessionsFlattened } from './__fixtures__/liveTrainingDataMock';

export interface MyLearningLoaderProps {
  myLearningResourceData: MyLearningResourceData | null;
  recentlyViewedLessonsResources: RecentlyViewedLessonResource[] | null;
  archivedContentResources: ArchivedContentResource[] | null;
  areUnseenNotificationsAvailable?: boolean;
  liveTrainingData: FlattenedLearningInstance[] | null;
}

export interface MyLearningResourceData {
  learningPaths: MyLearningResource[] | null;
  courses: MyLearningResource[] | null;
}

export interface MyLearningResource {
  completedDate?: string;
  startedDate?: string;
  contentfulId: string;
  progressTotal: number;
  subCollectionTotal: number;
  slug?: string;
  title: string;
  badgeUrl?: string;
  learningPathSlug?: string;
  learningPathContentfulId: string | null;
  product?: string;
  products?: string;
  productsList?: string[]; // Array of products for filtering (even when display shows "Multiple products")
  historicalNotifications: NotificationItem[];
  unseenNotifications: NotificationItem[];
  status?: LearningProgressStatus;
  isSaved?: boolean;
}

export interface RecentlyViewedLessonResource {
  contentfulId: string;
  lastVisitedDate: Date;
  progressTotal: number;
  subCollectionTotal: number;
  slug: string;
  title: string;
  courseSlug?: string;
  learningPathSlug?: string;
  duration: number;
  product: string;
  productsList?: string[]; // Array of products for filtering
  status: LearningProgressStatus;
}

export interface ArchivedContentResource {
  entryTitle: string;
  completionDate: Date;
}

interface SubLessonProgressMap {
  [lessonId: string]: LearningProgressStatus;
}

export const loader: LoaderFunction = async (): Promise<MyLearningLoaderProps> => {
  const recentlyViewedLessonsData = await getRecentlyViewedLessons();

  const retiredContentData = await getArchivedContent();

  //   progress data
  const inProgressData = await getMyLearning(MyLearningPageContentSections.IN_PROGRESS);
  const completedData = await getMyLearning(MyLearningPageContentSections.COMPLETE);
  const savedData = await getMyLearning(MyLearningPageContentSections.SAVED);

  // Track saved content IDs
  const savedContentfulIds = new Set([
    ...(savedData?.learningPath?.map((item) => item.contentfulId) ?? []),
    ...(savedData?.courses?.map((item) => item.contentfulId) ?? []),
  ]);

  // Use Sets for O(1) lookup performance instead of O(n) with includes()
  const inProgressContentfulIds = new Set([
    ...(inProgressData?.learningPath?.map((item) => item.contentfulId) ?? []),
    ...(inProgressData?.courses?.map((item) => item.contentfulId) ?? []),
  ]);
  const completedContentfulIds = new Set([
    ...(completedData?.learningPath?.map((item) => item.contentfulId) ?? []),
    ...(completedData?.courses?.map((item) => item.contentfulId) ?? []),
  ]);

  // saved items that are not in in-progress or completed
  const nonStartedSavedLearningData = {
    learningPath:
      savedData?.learningPath?.filter(
        (item) => !inProgressContentfulIds.has(item.contentfulId) && !completedContentfulIds.has(item.contentfulId),
      ) ?? [],
    courses:
      savedData?.courses?.filter(
        (item) => !inProgressContentfulIds.has(item.contentfulId) && !completedContentfulIds.has(item.contentfulId),
      ) ?? [],
  };

  const combinedMyLearningData = {
    learningPath: [
      ...(inProgressData?.learningPath ?? []),
      ...(completedData?.learningPath ?? []),
      ...(nonStartedSavedLearningData.learningPath ?? []),
    ],
    courses: [
      ...(inProgressData?.courses ?? []),
      ...(completedData?.courses ?? []),
      ...(nonStartedSavedLearningData.courses ?? []),
    ],
  };

  const mappedData = await getCoursesAndLearningPathsTitleAndSlugs(
    recentlyViewedLessonsData,
    combinedMyLearningData,
    retiredContentData,
    savedContentfulIds,
  );

  let transformedLiveTrainingData: FlattenedLearningInstance[] | null = null;

  try {
    // TODO: Replace with real API call when ready
    // const liveTrainingData = await getLiveTraining(0, 10);
    // const resolvedLiveTrainingData = resolveAlmRelationshipsByIdPattern(liveTrainingData as any);
    // transformedLiveTrainingData = transformToEnrolledLearningInstances(resolvedLiveTrainingData);

    // Using mock data temporarily
    transformedLiveTrainingData = allTrainingSessionsFlattened;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting live training data:', error);
  }

  return {
    ...mappedData,
    areUnseenNotificationsAvailable: false,
    liveTrainingData: transformedLiveTrainingData,
  };
};

//sort data by new notifications
export const sortCompletedData = (data: MyLearningData | null): MyLearningData => {
  const sortedPathData = data
    ? data.learningPath
      .sort((firstPath, secondPath) => firstPath.unseenNotifications.length - secondPath.unseenNotifications.length)
      .reverse()
    : [];

  const sortedCourseData = data
    ? data.courses
      .sort(
        (firstCourse, secondCourse) =>
          firstCourse.unseenNotifications.length - secondCourse.unseenNotifications.length,
      )
      .reverse()
    : [];

  return {
    learningPath: sortedPathData,
    courses: sortedCourseData,
  };
};

export const getCoursesAndLearningPathsTitleAndSlugs = async (
  recentlyViewedLessonsData: RecentlyViewedLessonItem[] | null,
  myLearningData: MyLearningData | null,
  archivedContentData: ArchivedContentResource[] | null,
  savedContentfulIds?: Set<string>,
): Promise<MyLearningLoaderProps> => {
  if (!recentlyViewedLessonsData && !myLearningData && !archivedContentData) {
    return {
      recentlyViewedLessonsResources: null,
      myLearningResourceData: null,
      archivedContentResources: null,
      liveTrainingData: null,
    };
  }

  const contentfulIds = getAllContentfulIds(recentlyViewedLessonsData, myLearningData, savedContentfulIds);

  const response = await fetchGraphQL<GetAllMyLearningEntriesQuery, GetAllMyLearningEntriesQueryVariables>(
    GET_ALL_MY_LEARNING_ENTRIES,
    { contentfulIds },
  );

  if (
    !response.smCourseCollection.items.length &&
    !response.learningPathCollection.items.length &&
    !response.smLessonCollection.items.length
  ) {
    return {
      recentlyViewedLessonsResources: null,
      myLearningResourceData: null,
      archivedContentResources: archivedContentData,
      liveTrainingData: null,
    };
  }

  const collectedData: CollectedDataItem[] = [
    ...response.smCourseCollection.items,
    ...response.learningPathCollection.items,
    ...response.smLessonCollection.items,
  ];

  const learningPathLessonIds =
    response.learningPathCollection.items.flatMap((item) =>
      item.coursesCollection.items.flatMap((course) => course.lessonsCollection.items.map((lesson) => lesson.sys.id)),
    ) ?? [];
  const courseLessonIds =
    response.smCourseCollection.items.flatMap((item) => item.lessonsCollection.items.map((lesson) => lesson.sys.id)) ??
    [];

  const subLessonProgressData = await getProgressForLessons([...learningPathLessonIds, ...courseLessonIds]);
  const subLessonProgressMap: SubLessonProgressMap =
    subLessonProgressData?.lessons.reduce((acc, lesson) => {
      acc[lesson.lessonId] = lesson.status;

      return acc;
    }, {} as SubLessonProgressMap) ?? {};

  return {
    recentlyViewedLessonsResources: transformRecentlyViewedLessonsData(recentlyViewedLessonsData, collectedData),
    myLearningResourceData: transformMyLearningData(
      myLearningData,
      collectedData,
      subLessonProgressMap,
      savedContentfulIds,
    ),
    archivedContentResources: archivedContentData,
    liveTrainingData: null,
  };
};

const getAllContentfulIds = (
  recentlyViewedLessonsData: RecentlyViewedLessonItem[] | null,
  myLearningData: MyLearningData | null,
  savedContentfulIds?: Set<string>,
): string[] => {
  const contentfulIds =
    recentlyViewedLessonsData
      ?.filter(
        (item: RecentlyViewedLessonItem) => !item.subCollectionTotal || item.progressTotal < item.subCollectionTotal,
      )
      .map((item) => item.contentfulId) ?? [];

  const courseContentfulIds =
    recentlyViewedLessonsData
      ?.filter((item) => item.courseContentfulId)
      .map((item) => item.courseContentfulId as string) ?? [];

  const learningPathContentfulIds =
    recentlyViewedLessonsData
      ?.filter((item) => item.learningPathContentfulId)
      .map((item) => item.learningPathContentfulId as string) ?? [];

  const myLearningContentfulIds =
    (myLearningData && [
      ...myLearningData.courses.map((item) => item.contentfulId),
      ...myLearningData.learningPath.map((item) => item.contentfulId),
    ]) ??
    [];

  const savedIds = savedContentfulIds ? Array.from(savedContentfulIds) : [];

  return [
    ...contentfulIds,
    ...courseContentfulIds,
    ...learningPathContentfulIds,
    ...myLearningContentfulIds,
    ...savedIds,
  ];
};

// TODO: filter out lessons that exist within courses or learning paths
const transformRecentlyViewedLessonsData = (
  recentlyViewedLessonsData: RecentlyViewedLessonItem[] | null,
  collectedData: CollectedDataItem[],
): RecentlyViewedLessonResource[] | null => {
  if (!recentlyViewedLessonsData) {
    return null;
  }

  return recentlyViewedLessonsData
    .map((item) => mapToRecentlyViewedLessonResource(item, collectedData))
    .filter((item): item is RecentlyViewedLessonResource => !!item);
};

const getLessonStatus = (lesson: RecentlyViewedLessonItem): LearningProgressStatus => {
  if (lesson.progressTotal === 0) {
    return LearningProgressStatus.NOT_STARTED;
  }
  if (lesson.progressTotal === lesson.subCollectionTotal) {
    return LearningProgressStatus.COMPLETED;
  }

  return LearningProgressStatus.IN_PROGRESS;
};

const mapToRecentlyViewedLessonResource = (
  item: RecentlyViewedLessonItem,
  collectedItems: CollectedDataItem[],
): RecentlyViewedLessonResource | null => {
  const matchingItem = collectedItems.find((value) => value.sys.id === item.contentfulId);
  const matchingCourseItem = collectedItems.find((value) => value.sys.id === item.courseContentfulId);
  const matchingLearningPathItem = collectedItems.find((value) => value.sys.id === item.learningPathContentfulId);

  if (matchingItem) {
    const lessonProducts = (matchingItem as LessonEntry).products;

    return {
      ...item,
      title: matchingItem.title,
      slug: getSlug(matchingItem),
      lastVisitedDate: new Date(item.lastVisitedDate),
      duration: (matchingItem as LessonEntry).duration,
      product: getContentProduct(lessonProducts, true),
      productsList: lessonProducts || [],
      status: getLessonStatus(item),
      subCollectionTotal: item.subCollectionTotal || (matchingItem as LessonEntry).sectionListCollection.total,
      ...(matchingCourseItem ? { courseSlug: getSlug(matchingCourseItem) } : {}),
      ...(matchingLearningPathItem ? { learningPathSlug: getSlug(matchingLearningPathItem) } : {}),
    };
  }

  return null;
};

export const transformMyLearningData = (
  myLearningData: MyLearningData | null,
  collectedItems: CollectedDataItem[],
  subLessonProgressMap: SubLessonProgressMap,
  savedContentfulIds?: Set<string>,
): MyLearningResourceData => {
  const learningPaths =
    myLearningData?.learningPath
      .map((item) => {
        const resource = mapToMyLearningResources(item, collectedItems, subLessonProgressMap);

        if (resource && savedContentfulIds) {
          resource.isSaved = savedContentfulIds.has(resource.contentfulId);
        }

        return resource;
      })
      .filter((item): item is MyLearningResource => !!item) ?? [];

  const courses =
    myLearningData?.courses
      .map((item) => {
        const resource = mapToMyLearningResources(item, collectedItems, subLessonProgressMap);

        if (resource && savedContentfulIds) {
          resource.isSaved = savedContentfulIds.has(resource.contentfulId);
        }

        return resource;
      })
      .filter((item): item is MyLearningResource => !!item) ?? [];

  return {
    learningPaths: learningPaths.length ? learningPaths : null,
    courses: courses.length ? courses : null,
  };
};

const getStatusForCourse = (
  course: CourseEntry,
  subLessonProgressMap: SubLessonProgressMap,
): LearningProgressStatus => {
  const lessons = course.lessonsCollection.items ?? [];

  // Check if any lesson is in progress
  const hasInProgressLesson = lessons.some(
    (lesson) => subLessonProgressMap[lesson.sys.id] === LearningProgressStatus.IN_PROGRESS,
  );

  if (hasInProgressLesson) {
    return LearningProgressStatus.IN_PROGRESS;
  }

  // Check if all lessons are completed
  const allLessonsCompleted = lessons.every(
    (lesson) => subLessonProgressMap[lesson.sys.id] === LearningProgressStatus.COMPLETED,
  );

  if (allLessonsCompleted) {
    return LearningProgressStatus.COMPLETED;
  }

  // If no lessons are started, it's not started
  return LearningProgressStatus.NOT_STARTED;
};

const mapToMyLearningResources = (
  item: MyLearningItem,
  collectedItems: CollectedDataItem[],
  subLessonProgressMap: SubLessonProgressMap,
): MyLearningResource | null => {
  const matchingItem = collectedItems.find((data) => data.sys.id === item.contentfulId) as MatchingDataItem | undefined;
  const matchingLearningPathItem = collectedItems.find((value) => value.sys.id === item.learningPathContentfulId);

  // append courses if learning path
  const learningPathProperties = (matchingItem as LearningPathEntry)?.coursesCollection
    ? {
      coursesCollection: {
        items: (matchingItem as LearningPathEntry).coursesCollection.items.map((course) => ({
          ...course,
          status: getStatusForCourse(course as CourseEntry, subLessonProgressMap),
        })),
      },
    }
    : {};

  // append lessons if course
  const courseProperties = (matchingItem as CourseEntry)?.lessonsCollection
    ? {
      lessonsCollection: {
        items: (matchingItem as CourseEntry).lessonsCollection.items.map((lesson) => ({
          ...lesson,
          status: subLessonProgressMap[lesson.sys.id],
        })),
      },
      status: getStatusForCourse(matchingItem as CourseEntry, subLessonProgressMap),
    }
    : {};

  if (matchingItem) {
    return {
      ...item,
      product: getContentProduct(matchingItem.product, true),
      productsList: matchingItem.product || [],
      title: matchingItem.title,
      slug: getSlug(matchingItem),
      badgeUrl: (matchingItem as CourseEntry).reward?.badgeCdnUrl,
      ...learningPathProperties,
      ...courseProperties,
      ...(matchingLearningPathItem ? { learningPathSlug: getSlug(matchingLearningPathItem) } : {}),
    };
  }

  return null;
};
